import { IconButton } from "@hortis/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@hortis/ui/dialog";
import { useMediaQuery } from "@hortis/ui/hooks/media-query";
import { XClose } from "@hortis/ui/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@hortis/ui/popover";
import { CircularProgress } from "@hortis/ui/progress";
import { useRouteContext } from "@tanstack/react-router";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import {
  AccessionsSearchColumn,
  useGetAccessionsQuery,
} from "generated/graphql";
import { type ReactNode, useRef, useState } from "react";
import { AccessionOption } from "src/features/new-material/accession-option";
import { useControlledDebounce } from "src/utils/hooks/debounce";
import { twMerge } from "tailwind-merge";

export const AccessionPickerLabel = ({ value }: { value: AccessionOption }) => (
  <div className="flex flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-grey-700">
    <span className="truncate text-grey-700">{value.accessionNumber}</span>
    <span className="font-normal text-grey-500">{value.scientificName}</span>
  </div>
);

const STARTS_WITH_NUMBER = /^\d.*$/;

export const AccessionPicker = ({
  value,
  onChange,
  placeholder,
  siteSlug,
  children,
  "data-cy": dataCy,
}: {
  value: AccessionOption | undefined | null;
  onChange: (val: AccessionOption | undefined | null) => void;
  placeholder?: string;
  children: ReactNode;
  siteSlug: string;
  "data-cy"?: string;
}) => {
  const isDesktop = useMediaQuery("md");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const { subdomain } = useRouteContext({ from: "__root__" });
  const searchTermDebouncer = useControlledDebounce((val: string) => {
    setDebouncedSearchTerm(val);
  });

  const [{ data, fetching }] = useGetAccessionsQuery({
    variables: {
      organisationSubdomain: subdomain,
      collectionSiteSlug: siteSlug,
      last: 50,
      searchTerm: {
        field: STARTS_WITH_NUMBER.test(debouncedSearchTerm)
          ? AccessionsSearchColumn.AccessionNum
          : AccessionsSearchColumn.ScientificName,
        value: debouncedSearchTerm,
      },
      filters: { excludeDeaccessioned: true },
    },
  });

  const accessions = data?.org?.site?.result?.nodes;

  const handleChange = (accession: AccessionOption) => {
    if (value?.id === accession.id) {
      onChange(null);
    } else {
      onChange(accession);
    }
    setOpen(false);
  };

  const handleSearchChange = (val: string) => {
    setSearch(val);
    searchTermDebouncer.schedule(val);
  };

  const onOpenChange = (val: boolean) => {
    if (!val) {
      handleSearchChange("");
    }
    setOpen(val);
  };

  const commandContent = (
    <>
      <CommandEmpty className="flex items-center justify-center pt-2 font-medium text-sm">
        {accessions?.length === 0 ? (
          "No results"
        ) : (
          <CircularProgress size={20} />
        )}
      </CommandEmpty>
      <CommandList className="px-0 py-0">
        {accessions?.length !== 0 && (
          <CommandGroup className="p-1">
            {accessions?.map((accession) => {
              const matches = match(
                accession.taxon?.scientificName ?? "",
                search,
                {
                  insideWords: true,
                },
              );
              const parts = parse(
                accession.taxon?.scientificName ?? "",
                matches,
              );
              const selected = value?.id === accession.id;
              return (
                <CommandItem
                  key={accession.id}
                  value={accession.id}
                  keywords={[
                    accession.taxon?.scientificName ?? "",
                    accession.accessionNumber,
                  ]}
                  onSelect={() => {
                    handleChange({
                      id: accession.id,
                      scientificName: accession.scientificName ?? "",
                      accessionNumber: accession.accessionNumber,
                    });
                    handleSearchChange("");
                  }}
                  data-cy="accession-picker-item"
                  selected={selected}
                  className="flex w-full"
                >
                  <div className="truncate font-normal text-grey-500">
                    {accession.accessionNumber}
                  </div>
                  <div className="truncate">
                    {parts.map((part, index) => (
                      <span
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className={twMerge(
                          part.highlight && "font-bold text-grey-900",
                        )}
                      >
                        {part.text}
                      </span>
                    ))}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}
      </CommandList>
    </>
  );

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          data-cy={dataCy ?? "accession-picker"}
          asChild
          ref={buttonRef}
        >
          {children}
        </PopoverTrigger>
        <PopoverContent className="z-99999 min-w-(--radix-popover-trigger-width)!">
          <Command
            filter={(value, search, keywords) => {
              const extendValue = keywords?.join(" ") ?? "";
              if (
                extendValue.toLowerCase().includes(search.toLowerCase()) ||
                value === "new-accession"
              ) {
                return 1;
              }
              return 0;
            }}
          >
            <CommandInput
              placeholder={placeholder ?? "Select accession..."}
              value={search}
              onValueChange={handleSearchChange}
              fetching={fetching}
              data-cy="accession-picker-input"
            />
            {commandContent}
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger
        data-cy={dataCy ?? "accession-picker"}
        asChild
        ref={buttonRef}
      >
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-none p-0!">
        <Command
          size="lg"
          filter={(value, search, keywords) => {
            const extendValue = keywords?.join(" ") ?? "";
            if (
              extendValue.toLowerCase().includes(search.toLowerCase()) ||
              value === "new-accession"
            ) {
              return 1;
            }
            return 0;
          }}
        >
          <CommandInput
            placeholder={placeholder ?? "Select accession..."}
            value={search}
            onValueChange={handleSearchChange}
            fetching={fetching}
            data-cy="accession-picker-input"
          >
            <DialogClose asChild>
              <IconButton
                icon={XClose}
                size="xs"
                ariaLabel="Close"
                variant="tertiaryGray"
              />
            </DialogClose>
          </CommandInput>
          {commandContent}
        </Command>
      </DialogContent>
    </Dialog>
  );
};
